import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button, Input, Switch, Radio, RadioGroup } from '@nextui-org/react';
import { ArrowLeft, Layers, Plus, Calendar, MapPin, Share2, Trophy } from 'lucide-react';
import seriesService from '../services/seriesService';

const TEAL = '#3FCED2';

const FEATURES = [
  {
    icon: <Layers className="w-6 h-6" />,
    title: 'SERIEN-PROFIL',
    text: 'Erstelle ein detailliertes Profil für deine Turnierserie mit Zeitplan und Spielmodus.',
  },
  {
    icon: <Calendar className="w-6 h-6" />,
    title: 'AUTOMATISCHE TERMINE',
    text: 'Generiere wiederkehrende Termine automatisch oder lege individuelle Daten fest.',
  },
  {
    icon: <MapPin className="w-6 h-6" />,
    title: 'STANDORT & ADRESSE',
    text: 'Spieler finden deine Serie über die Karten- und Standortsuche.',
  },
  {
    icon: <Share2 className="w-6 h-6" />,
    title: 'ANMELDUNG & TEILNAHME',
    text: 'Aktiviere die DartCloud-Anmeldung oder verlinke auf ein externes System.',
  },
];

type RegistrationMode = 'dartcloud' | 'external' | 'none';

const CreateSeriesPage: React.FC = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [steeldart, setSteeldart] = useState(false);
  const [edart, setEdart] = useState(false);
  const [online, setOnline] = useState(false);
  const [registrationMode, setRegistrationMode] = useState<RegistrationMode>('dartcloud');
  const [entryLink, setEntryLink] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Bitte einen Namen festlegen');
      return;
    }
    if (name.trim().length < 3) {
      setError('Der Name muss mindestens 3 Zeichen lang sein');
      return;
    }
    if (!steeldart && !edart && !online) {
      setError('Bitte wähle mindestens eine Kategorie aus');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const categories: string[] = [];
      if (steeldart) categories.push('steeldart');
      if (edart) categories.push('edart');
      if (online) categories.push('online');

      let registrationType: 'DARTCLOUD' | 'EXTERNAL' | 'NONE' = 'DARTCLOUD';
      if (registrationMode === 'external') registrationType = 'EXTERNAL';
      if (registrationMode === 'none') registrationType = 'NONE';

      const payload: any = {
        name: name.trim(),
        categories,
        registrationType,
        entryEnabled: registrationMode === 'dartcloud',
      };

      if (registrationMode === 'external' && entryLink.trim()) {
        payload.entryLink = entryLink.trim();
      }

      const res = await seriesService.createSeries(payload);
      const created = (res as any)?.data ?? res;
      const id = created?._id ?? created?.id;
      if (id) navigate(`/verwaltung/turnierserie/${id}/bearbeiten`);
      else navigate('/verwaltung?tab=series');
    } catch (err: any) {
      console.error(err);
      setError(err?.response?.data?.message || 'Turnierserie konnte nicht erstellt werden');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Helmet>
        <title>Turnierserie erstellen - DartCloud</title>
      </Helmet>

      <div className="sticky top-0 z-10 bg-black/80 backdrop-blur border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate('/verwaltung?tab=series')}
            className="text-zinc-300 hover:text-white p-1"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-lg font-bold flex-1">Turnierserie erstellen</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-8">
        <section className="text-center py-6">
          <div
            className="inline-flex w-20 h-20 rounded-full items-center justify-center mb-4"
            style={{ backgroundColor: `${TEAL}22`, color: TEAL }}
          >
            <Layers size={40} />
          </div>
          <h2 className="text-2xl font-bold mb-2">Deine Turnierserie in der DartCloud</h2>
          <p className="text-sm text-zinc-400 max-w-xl mx-auto">
            Erstelle eine regelmäßige Turnierserie und gewinne Teilnehmer aus der Dart-Community.
          </p>
        </section>

        <section className="space-y-4">
          <Input
            label="Name der Turnierserie"
            placeholder="z.B. Montagsliga Steeldart"
            value={name}
            onValueChange={(v) => {
              setName(v);
              setError(null);
            }}
            classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }}
            isRequired
          />

          <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4 space-y-3">
            <div className="text-sm font-semibold">Kategorien *</div>
            <p className="text-xs text-zinc-500 -mt-1">Wähle eine oder mehrere Kategorien aus</p>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">Steeldart</div>
                <div className="text-xs text-zinc-500">Klassisches Dart mit Stahlspitzen</div>
              </div>
              <Switch
                isSelected={steeldart}
                onValueChange={(v) => {
                  setSteeldart(v);
                  setError(null);
                }}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">E-Dart</div>
                <div className="text-xs text-zinc-500">Elektronisches Dart</div>
              </div>
              <Switch
                isSelected={edart}
                onValueChange={(v) => {
                  setEdart(v);
                  setError(null);
                }}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">Online</div>
                <div className="text-xs text-zinc-500">Online-Turnierserie (z.B. Webcam Darts)</div>
              </div>
              <Switch
                isSelected={online}
                onValueChange={(v) => {
                  setOnline(v);
                  setError(null);
                }}
              />
            </div>
          </div>

          <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4 space-y-3">
            <div className="text-sm font-semibold">Anmeldung *</div>
            <p className="text-xs text-zinc-500 -mt-1">Wie können sich Teilnehmer anmelden?</p>
            <RadioGroup
              value={registrationMode}
              onValueChange={(v) => setRegistrationMode(v as RegistrationMode)}
              classNames={{ wrapper: 'gap-2' }}
            >
              <Radio value="dartcloud" description="Teilnehmer melden sich direkt über DartCloud an">
                DartCloud Anmeldung
              </Radio>
              <Radio value="external" description="Weiterleitung zu einer externen Anmeldeseite">
                Externer Link
              </Radio>
              <Radio value="none" description="Turnierserie ohne Online-Anmeldung">
                Keine Anmeldung
              </Radio>
            </RadioGroup>

            {registrationMode === 'external' && (
              <Input
                label="Externer Link"
                placeholder="https://..."
                value={entryLink}
                onValueChange={setEntryLink}
                classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }}
              />
            )}
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-300 rounded-xl p-3 text-sm">
              {error}
            </div>
          )}

          <div
            className="rounded-xl p-3 text-sm flex items-start gap-2"
            style={{ backgroundColor: `${TEAL}11`, color: TEAL, border: `1px solid ${TEAL}33` }}
          >
            <Trophy size={16} className="mt-0.5 flex-shrink-0" />
            <span className="text-zinc-300">
              Weitere Details wie Spielmodus, Startgebühr, Adresse, Uhrzeiten und Beschreibung kannst
              du nach dem Erstellen bearbeiten.
            </span>
          </div>
        </section>

        <section className="space-y-3">
          <h3 className="text-base font-semibold">Vorteile</h3>
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4 flex items-start gap-4"
            >
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${TEAL}22`, color: TEAL }}
              >
                {f.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold tracking-wide mb-1">{f.title}</div>
                <p className="text-sm text-zinc-400">{f.text}</p>
              </div>
            </div>
          ))}
        </section>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-black/95 backdrop-blur border-t border-zinc-800 z-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3">
          <Button
            className="w-full"
            isDisabled={saving || !name.trim()}
            isLoading={saving}
            onPress={handleCreate}
            startContent={!saving ? <Plus size={16} /> : null}
            style={{ backgroundColor: TEAL, color: '#000' }}
          >
            Turnierserie erstellen
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateSeriesPage;
